import * as Yup from 'yup';
import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import eyeFill from '@iconify/icons-eva/eye-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MIconButton } from '../../@material-extend';
import { useTranslation } from 'react-i18next';
import axios from '../../../utils/axios';
import { apiConfig } from '../../../config';
import '../../../assets/css/general.scss';
import { useNavigate } from 'react-router';
import { User } from '../../../@types/model/user';
import user from 'redux/slices/user';
import { useSelector, RootState } from 'redux/store';

// ----------------------------------------------------------------------

type InitialValues = {
  password: string;
  passwordConfirmation: string;
  afterSubmit?: string;
  name: string;
};

export default function UpdateForm({ userData }: { userData: User }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingMessage, setSubmittingMessage] = useState('');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);

  const RegisterSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, t('password_rules'))
      .max(150, t('password_rules'))
      .matches(/^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, t('password_rules'))
      .required(t('required')),
    passwordConfirmation: Yup.string()
      .required('Repite la contraseña')
      .oneOf([Yup.ref('password'), null], 'Las contraseñas deben ser iguales'),
    name: Yup.string().required(t('required'))
  });

  const formik = useFormik<InitialValues>({
    initialValues: {
      password: '',
      passwordConfirmation: '',
      name: userData.attributes.name
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values, { setErrors }) => {
      setSubmittingMessage(t('update_profile_load'));
      setSubmitting(true);

      axios
        .put('/api/v2/user/update/password', {
          password: values.password,
          plain_password: values.password,
          guid: userData.id,
          email: userData.attributes.email,
          name: values.name,
          roles: userData.attributes.roles,
          workspace: userData.attributes.workspaces[0]
        })
        .then((data) => {
          enqueueSnackbar(t('update_profile_success'), {
            variant: 'success',
            action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });
          setSubmitting(false);
          setTimeout(() => {
            if (user?.attributes.roles.includes('ROLE_END_USER')) {
              navigate('/end-user');
            } else {
              navigate('/');
            }
          }, 1500);
        })
        .catch((e) => {
          enqueueSnackbar(e.detail ?? t('error'), {
            variant: 'error',
            action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });
          setSubmitting(false);
        });
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        {submitting ? (
          <div className="progress-message">
            <CircularProgress className="spinner" />
            <div className="message">{submittingMessage}</div>
          </div>
        ) : null}
        <Stack spacing={3}>
          {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

          <TextField
            fullWidth
            label={t('name')}
            {...getFieldProps('name')}
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name && errors.name}
          />

          <TextField
            fullWidth
            disabled
            autoComplete="email"
            type="email"
            label={t('email')}
            {...getFieldProps('email')}
            value={userData.attributes.email}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label={t('password')}
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />

          <TextField
            fullWidth
            {...getFieldProps('passwordConfirmation')}
            type="password"
            label="Repita la contraseña"
            error={Boolean(touched.passwordConfirmation && errors.passwordConfirmation)}
            helperText={touched.passwordConfirmation && errors.passwordConfirmation}
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            {t('update')}
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
